/**
 * Hook: Speech-to-text via WebSpeech API
 */
import { useRef, useState } from 'react'

export function useSpeechInput(onResult: (text: string) => void, lang?: string) {
  const [listening, setListening] = useState(false)
  const recognitionRef = useRef<unknown>(null)

  const toggle = () => {
    const win = window as Window & {
      webkitSpeechRecognition?: new () => {
        lang: string; continuous: boolean; interimResults: boolean
        onresult: ((e: { results: { 0: { 0: { transcript: string } } }[] }) => void) | null
        onerror: (() => void) | null
        onend: (() => void) | null
        start: () => void
        stop: () => void
      }
      SpeechRecognition?: new () => {
        lang: string; continuous: boolean; interimResults: boolean
        onresult: ((e: { results: { 0: { 0: { transcript: string } } }[] }) => void) | null
        onerror: (() => void) | null
        onend: (() => void) | null
        start: () => void
        stop: () => void
      }
    }

    const SpeechRecognitionClass = win.webkitSpeechRecognition ?? win.SpeechRecognition

    if (!SpeechRecognitionClass) {
      console.warn('Speech recognition not supported in this browser.')
      return
    }

    if (listening) {
      const r = recognitionRef.current as { stop: () => void } | null
      r?.stop()
      setListening(false)
      return
    }

    const recognition = new SpeechRecognitionClass()
    const langMap: Record<string, string> = { tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES' }
    recognition.lang = langMap[lang ?? ''] ?? 'en-US'
    recognition.continuous = false
    recognition.interimResults = false
    recognition.onresult = (e) => {
      const result = (e.results[0] as unknown as { 0: { transcript: string } })
      onResult(result[0].transcript)
      setListening(false)
    }
    recognition.onerror = () => setListening(false)
    recognition.onend = () => setListening(false)
    recognitionRef.current = recognition
    recognition.start()
    setListening(true)
  }

  return { listening, toggle }
}
